import { useCallback, useEffect, useRef, useState } from "react";
import { abdmAPI } from "../services/abdm.api";

export const encryptTextApi = async (plaintext) => {
    try {
        if (!plaintext) {
            throw new Error('Plaintext is required for encryption');
        }
        const response = await abdmAPI.encryptAPI({
            plaintext: plaintext.toString()
        });

        if (!response?.success || !response?.encryptText) {
            throw new Error(response?.message || 'Failed to encrypt data');
        }
        return response?.encryptText;
    } catch (error) {
        throw new Error(error?.response?.message || error?.message || 'Encryption failed');
    }
};

export const fetchAbdmAccessToken = async (setIsLoading, toast) => {
    try {
        setIsLoading(true);

        const response = await abdmAPI.abdmAccessToken();

        if (response?.success && response?.accessToken) {
            return response?.accessToken;
        } else {
            throw new Error(response?.message || 'Failed to fetch ABDM access token');
        }
    } catch (error) {
        toast.error(error?.response?.message || error?.message || 'Failed to fetch ABDM access token');
        return null;
    } finally {
        setIsLoading(false);
    }
};

// Format ABHA ID - mask all except last 4 digits with hyphens
export const formatAbhaId = (abhaId) => {
    if (!abhaId) return "N/A";

    // Remove any existing separators (hyphens or spaces)
    const cleaned = abhaId.replace(/[-\s]/g, '');

    // Get last 4 digits
    const last4 = cleaned.slice(-4);

    // Mask all except last 4 with 'x'
    const masked = 'x'.repeat(cleaned.length - 4) + last4;

    // Format as "xx-xxxx-xxxx-1234" with hyphens
    // Assuming total length is 14 digits (like 91-7704-3502-3039)
    if (masked.length === 14) {
        return `${masked.slice(0, 2)}-${masked.slice(2, 6)}-${masked.slice(6, 10)}-${masked.slice(10, 14)}`;
    }

    // For 12 digit format (without country code)
    if (masked.length === 12) {
        return `${masked.slice(0, 2)}-${masked.slice(2, 6)}-${masked.slice(6, 10)}-${masked.slice(10, 12)}`;
    }

    // If different length, just add spaces every 4 characters
    return masked.match(/.{1,4}/g)?.join(' ') || masked;
};

// Format ABHA ID with hyphens: XX-XXXX-XXXX-XXXX
export const formatAbhaInput = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Limit to 14 digits
    const limited = digits.slice(0, 14);

    // Add hyphens at appropriate positions
    let formatted = '';
    if (limited.length > 0) {
        formatted = limited.slice(0, 2);
        if (limited.length > 2) {
            formatted += '-' + limited.slice(2, 6);
        }
        if (limited.length > 6) {
            formatted += '-' + limited.slice(6, 10);
        }
        if (limited.length > 10) {
            formatted += '-' + limited.slice(10, 14);
        }
    }
    return formatted;
};

export const useResendOTP = ({
    maxResendCount = 3,
    countdownSeconds = 60,
    onResend = null,
    autoStart = true
} = {}) => {
    const [countdown, setCountdown] = useState(countdownSeconds);
    const [resendCount, setResendCount] = useState(1);
    const [isTimerActive, setIsTimerActive] = useState(autoStart);
    const intervalRef = useRef(null);

    // Format time as MM:SS
    const formattedTime = useCallback(() => {
        const minutes = Math.floor(countdown / 60);
        const seconds = countdown % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }, [countdown]);

    // Check if resend is allowed
    const canResend = countdown === 0 && resendCount < maxResendCount;

    // Timer effect
    useEffect(() => {
        if (isTimerActive && countdown > 0) {
            intervalRef.current = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(intervalRef.current);
                        setIsTimerActive(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isTimerActive, countdown]);

    // Start timer function
    const startTimer = useCallback(() => {
        setCountdown(countdownSeconds);
        setIsTimerActive(true);
    }, [countdownSeconds]);

    // Handle resend
    const handleResend = useCallback(async () => {
        if (!canResend) return;

        try {
            // Execute the onResend callback if provided
            if (onResend && typeof onResend === 'function') {
                await onResend();
            }

            // Only increment count and restart timer if API call succeeded
            setResendCount((prev) => prev + 1);

            // Restart the timer only after successful API call
            startTimer();
        } catch (error) {
            console.error('Resend OTP error:', error);
            // Don't increment count or restart timer if API call failed
            throw error; // Re-throw so calling component can handle it
        }
    }, [canResend, onResend, startTimer]);

    // Reset timer and counter
    const resetTimer = useCallback(() => {
        setCountdown(countdownSeconds);
        setResendCount(1);
        setIsTimerActive(autoStart);
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
    }, [countdownSeconds, autoStart]);

    return {
        countdown,
        resendCount,
        canResend,
        formattedTime: formattedTime(),
        handleResend,
        resetTimer,
        startTimer,
        isTimerActive,
        maxResendCount
    };
};

// Mask ABHA number - mask all except last 4 digits
export const maskNumber = (num) => {
    // Split the number into parts based on the delimiter '-'
    const parts = num.split("-");
    // Mask all but the last group
    const maskedParts = parts.map((part, index) =>
        index === parts.length - 1 ? part : part.replace(/./g, "X")
    );
    // Join the parts back with the delimiter
    return maskedParts.join("-");
};

// Process ABHA profile data and store in localStorage
export const processAbhaProfileData = (abhaProfileData) => {
    if (!abhaProfileData?.name) {
        return null;
    }

    let dateofbirth =
        abhaProfileData.dayOfBirth +
        "-" +
        abhaProfileData.monthOfBirth +
        "-" +
        abhaProfileData.yearOfBirth;

    const base64Prefix = "data:image/png;base64,";
    const base64String = abhaProfileData.profilePhoto
        ? base64Prefix + abhaProfileData.profilePhoto
        : abhaProfileData.kycPhoto
            ? base64Prefix + abhaProfileData.kycPhoto
            : (abhaProfileData.gender === 'M') ? '/images/male.svg' : (abhaProfileData.gender === 'F') ? '/images/female.svg' : 'Other';

    const beneficiaryDetails = {
        name: abhaProfileData.name,
        abhaNo: maskNumber(abhaProfileData.ABHANumber),
        originalabha: abhaProfileData.ABHANumber,
        hid: abhaProfileData.preferredAbhaAddress,
        hidn: abhaProfileData.ABHANumber,
        address: abhaProfileData.address,
        mobile: abhaProfileData.mobile.slice(0, -4).replace(/./g, "X") + abhaProfileData.mobile.slice(-4),
        dob: dateofbirth,
        yearOfBirth: 'XX-XX-' + abhaProfileData.yearOfBirth,
        gender: (abhaProfileData.gender === 'M') ? 'Male' : (abhaProfileData.gender === 'F') ? 'Female' : 'Other',
        kycphoto: base64String,
        profilePhoto: abhaProfileData?.profilePhoto,
        originalKycPhoto: abhaProfileData?.kycPhoto,
        preferredAbhaAddress: abhaProfileData?.preferredAbhaAddress,
        email: abhaProfileData?.email,
        abhaNumber: abhaProfileData?.ABHANumber,
        statelgd: abhaProfileData.stateCode,
        districtlgd: abhaProfileData.districtCode,
        mobileOriginal: abhaProfileData.mobile,
    };

    const orignalBeneficiaryData = {
        distlgd: abhaProfileData.districtCode,
        district_name: abhaProfileData.districtName,
        dob: dateofbirth,
        gender: abhaProfileData.gender,
        fullgendername: (abhaProfileData.gender === 'M') ? 'Male' : (abhaProfileData.gender === 'F') ? 'Female' : 'Other',
        hid: abhaProfileData.preferredAbhaAddress,
        hidn: abhaProfileData.ABHANumber,
        mobile: abhaProfileData.mobile,
        name: abhaProfileData.name,
        statename: abhaProfileData.stateName,
        statelgd: abhaProfileData.stateCode,
        address: abhaProfileData.address,
        kycphoto: base64String,
        profilePhoto: abhaProfileData?.profilePhoto || null,
        originalKycPhoto: abhaProfileData?.kycPhoto,
        preferredAbhaAddress: abhaProfileData?.preferredAbhaAddress || null,
        email: abhaProfileData?.email || null,
        abhaNumber: abhaProfileData?.ABHANumber || null,
        districtlgd: abhaProfileData.districtCode,
        mobileOriginal: abhaProfileData.mobile,
    };

    localStorage.setItem("beneficiaryDetails", JSON.stringify(beneficiaryDetails));
    localStorage.setItem("orignalBeneficiaryData", JSON.stringify(orignalBeneficiaryData));
    localStorage.removeItem("NCD_Searchdetails");
    localStorage.removeItem("NIKSHAY_Registration_Detials");
    localStorage.removeItem("NIKSHAY_NEW_PROFILE");
    return {
        beneficiaryDetails,
        orignalBeneficiaryData
    };
};

/**
 * Verhoeff Algorithm tables for Aadhaar validation
 */
const d = [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
    [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
    [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
    [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
    [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
    [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
    [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
    [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
    [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];

const p = [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
    [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
    [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
    [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
    [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
    [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
    [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

/**
 * Validate number using Verhoeff Algorithm
 * @param {string} num 
 * @returns {boolean}
 */
const validateVerhoeff = (num) => {
    let c = 0;
    const myArray = num.split("").reverse().map(Number);

    for (let i = 0; i < myArray.length; i++) {
        c = d[c][p[i % 8][myArray[i]]];
    }
    return c === 0;
};

/**
 * Validate Aadhaar number using Verhoeff Algorithm
 * @param {string} aadhaarNumber 
 * @returns {Object} { isValid: boolean, cleanAadhaar: string }
 */
export const validateAadhaar = (aadhaarNumber) => {
    if (!aadhaarNumber) return { isValid: false, cleanAadhaar: "" };
    const cleanAadhaar = aadhaarNumber.replace(/\D/g, "");

    // Check if it's 12 digits
    if (cleanAadhaar.length !== 12) {
        return { isValid: false, cleanAadhaar };
    }

    // Validate using Verhoeff Algorithm
    return { isValid: validateVerhoeff(cleanAadhaar), cleanAadhaar };
};

/**
 * Get helper text for Aadhaar validation
 * @param {string} aadhaar 
 * @param {string} cleanAadhaar 
 * @param {boolean} isValid 
 * @returns {string}
 */
export const getAadhaarHelperText = (aadhaar, cleanAadhaar, isValid) => {
    if (!aadhaar || aadhaar.length === 0) return "";

    if (cleanAadhaar.length < 12) {
        return "Please enter a valid 12-digit Aadhaar number";
    }

    if (!isValid) {
        return "Invalid Aadhaar number";
    }

    return "";
};